import type { Task } from './tasksApi';
import type { Reminder } from './remindersApi';
import type { CalendarEvent } from './calendarApi';
import type { VideoMeetingSummary } from './meetApi';

/**
 * Work that still needs doing: "To do" and "In progress". A task in review has
 * left the assignee's hands and a finished one is finished, so neither belongs
 * in a day's "Total estimat" — that number exists to answer
 * "how much do I still have on my plate today?".
 *
 * Statuses are configurable, and "In review" shares the STARTED category with
 * "In progress" (the daily check-in relies on that), so the category alone
 * cannot tell them apart; the seeded review status is excluded by its stable
 * slug. BACKLOG is not "To do" and is left out too.
 */
export function isOpenWork(task: Task): boolean {
  if (task.status === 'in_review') return false;
  return task.statusCategory === 'UNSTARTED' || task.statusCategory === 'STARTED';
}

export interface DayTotals {
  /** Everything with a duration on the day, whatever its state — the day as planned. */
  estimated: number;
  /** What is still ahead: open tasks, plus timed items whose scheduled time has not come. */
  remaining: number;
}

/**
 * The two numbers in a day's header (Andrei, 2026-09-30), both in decimal hours:
 *
 * - "Total estimat": every task with an estimate regardless of status (Done,
 *   In review, In progress, To do…), every open reminder with a duration,
 *   every Firmio Meet meeting that is not cancelled and every timed Google
 *   Calendar event that is not cancelled. The day as it was planned.
 * - "Total rămas": only what is still to be done — tasks in To do / In
 *   progress (isOpenWork), and reminders, meetings and events whose scheduled
 *   time is still ahead of `nowMs`. Once the hour of a meeting or event has
 *   passed it is no longer "remaining", whatever its state.
 *
 * Null when nothing on the day carries any time.
 */
export function dayWorkTotals(
  tasks: Task[],
  reminders: Reminder[] = [],
  events: CalendarEvent[] = [],
  meetings: VideoMeetingSummary[] = [],
  nowMs: number = Date.now(),
): DayTotals | null {
  let estimated = 0;
  let remaining = 0;
  let any = false;
  const count = (hours: number, ahead: boolean) => {
    any = true;
    estimated += hours;
    if (ahead) remaining += hours;
  };
  for (const task of tasks) {
    if (task.estimate == null) continue;
    count(task.estimate, isOpenWork(task));
  }
  for (const reminder of reminders) {
    if (reminder.estimatedMinutes == null || reminder.done) continue;
    count(reminder.estimatedMinutes / 60, Date.parse(reminder.dueAt) > nowMs);
  }
  for (const meeting of meetings) {
    if (meeting.status === 'CANCELLED' || !(meeting.durationMinutes > 0)) continue;
    count(meeting.durationMinutes / 60, Date.parse(meeting.scheduledAt) > nowMs);
  }
  for (const event of events) {
    if (event.allDay || event.status === 'CANCELLED') continue;
    const startMs = Date.parse(event.startTime);
    const ms = Date.parse(event.endTime) - startMs;
    if (!(ms > 0)) continue;
    count(ms / 3_600_000, startMs > nowMs);
  }
  return any ? { estimated, remaining } : null;
}
